import { useState, useRef, useEffect } from "react";
import { Bell, CheckCircle, Ticket, UserPlus, ArrowsClockwise, X, Checks } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/hooks/useNotifications";

const iconMap = {
  Ticket: Ticket,
  UserPlus: UserPlus,
  RefreshCw: ArrowsClockwise,
  Bell: Bell,
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "ahora";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

export const NotificationBell = ({ onNavigate }) => {
  const { user } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleNotificationClick = (notif) => {
    if (user && !(notif.read_by || []).includes(user.id)) {
      markAsRead(notif.id);
    }
    if (notif.link && onNavigate) {
      onNavigate(notif.link);
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-container">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell size={22} weight={unreadCount > 0 ? "fill" : "regular"} className="text-slate-600" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 animate-pulse"
            data-testid="notification-unread-badge"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[380px] max-h-[480px] bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden flex flex-col"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/80">
            <h3 className="font-semibold text-slate-800 text-sm">Notificaciones</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  data-testid="mark-all-read-btn"
                >
                  <Checks size={14} /> Marcar todas
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 hover:bg-slate-200 rounded"
                data-testid="close-notifications-btn"
              >
                <X size={16} className="text-slate-400" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1" data-testid="notification-list">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Bell size={36} className="mb-2 opacity-40" />
                <p className="text-sm">Sin notificaciones</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const isRead = (notif.read_by || []).includes(user?.id);
                const IconComp = iconMap[notif.icon] || Bell;
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-slate-50 transition-colors hover:bg-slate-50 ${
                      isRead ? "opacity-60" : "bg-blue-50/40"
                    }`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div
                      className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5"
                      style={{ backgroundColor: `${notif.color || "#6366F1"}20` }}
                    >
                      <IconComp size={16} weight="bold" style={{ color: notif.color || "#6366F1" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-800 truncate">{notif.title}</span>
                        <span className="text-[10px] text-slate-400 flex-shrink-0">{timeAgo(notif.created_at)}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                    </div>
                    {!isRead && (
                      <div className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-2" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
